import React, { useCallback, useState } from 'react';

import { Alert, Box, Button, Stack, Typography } from '@mui/material';

import RuleAccordionItem from '~/components/Accommodation/Pages/Rules/RuleAccordionItem';
import RuleForm, { RuleFormValues } from '~/components/Accommodation/Pages/Rules/RuleForm';
import { summarizeCriteria } from '~/components/Accommodation/Pages/Rules/ruleCriteriaSummary';

import {
  useCreateCampaignRule,
  useDeleteCampaignRule,
  useGetCampaignRules,
  useUpdateCampaignRule,
} from '~/hooks/apis/accommodation/useCampaignRules';

interface Props {
  campaignId: string;
}

function AccommodationCampaignRulesTab({ campaignId }: Props) {
  const { data: rules = [], isLoading, error } = useGetCampaignRules({ campaignId });
  const createRuleMutation = useCreateCampaignRule();
  const updateRuleMutation = useUpdateCampaignRule();
  const deleteRuleMutation = useDeleteCampaignRule();

  const [expandedRuleId, setExpandedRuleId] = useState<string | null>(null);
  const [isAddingRule, setIsAddingRule] = useState(false);

  const handleToggle = useCallback((ruleId: string) => {
    setExpandedRuleId((prev) => (prev === ruleId ? null : ruleId));
    setIsAddingRule(false);
  }, []);

  const handleAddRule = useCallback(() => {
    setIsAddingRule(true);
    setExpandedRuleId(null);
  }, []);

  const handleCreate = useCallback(
    async (values: RuleFormValues) => {
      await createRuleMutation.mutateAsync({
        campaignId,
        payload: { name: values.name, description: values.description ?? undefined, criteria: values.criteria },
      });
      setIsAddingRule(false);
    },
    [campaignId, createRuleMutation],
  );

  const handleUpdate = useCallback(
    (ruleId: string) => async (values: RuleFormValues) => {
      await updateRuleMutation.mutateAsync({
        campaignId,
        ruleId,
        payload: { name: values.name, description: values.description, criteria: values.criteria },
      });
      setExpandedRuleId(null);
    },
    [campaignId, updateRuleMutation],
  );

  const handleDelete = useCallback(
    (ruleId: string) => async () => {
      await deleteRuleMutation.mutateAsync({ campaignId, ruleId });
      setExpandedRuleId(null);
    },
    [campaignId, deleteRuleMutation],
  );

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Rules gate whether this campaign applies to a rate. A rate must satisfy every condition on at least one rule (or
        the campaign applies unconditionally if it has no rules).
      </Typography>

      {error && <Alert severity="error">Failed to load rules: {error.message}</Alert>}

      {!isLoading && rules.length === 0 && !isAddingRule && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No rules yet — this campaign applies unconditionally.
        </Alert>
      )}

      <Stack spacing={1}>
        {rules.map((rule) => (
          <RuleAccordionItem
            key={rule.id}
            title={rule.name}
            subtitle={summarizeCriteria(rule.criteria)}
            expanded={expandedRuleId === rule.id}
            onToggle={() => handleToggle(rule.id)}
          >
            <RuleForm
              initialValues={{ name: rule.name, description: rule.description, criteria: rule.criteria }}
              onSave={handleUpdate(rule.id)}
              onDelete={handleDelete(rule.id)}
              onCancel={() => setExpandedRuleId(null)}
            />
          </RuleAccordionItem>
        ))}

        {isAddingRule && (
          <RuleAccordionItem title="New rule" subtitle="Not yet saved" expanded onToggle={() => setIsAddingRule(false)}>
            <RuleForm initialValues={null} onSave={handleCreate} onCancel={() => setIsAddingRule(false)} />
          </RuleAccordionItem>
        )}
      </Stack>

      {!isAddingRule && (
        <Box mt={2}>
          <Button variant="outlined" onClick={handleAddRule}>
            + Add rule
          </Button>
        </Box>
      )}
    </Box>
  );
}

export default AccommodationCampaignRulesTab;
